import { parseHhmmToMinutes } from './_internal/parseHhmm'

const MINUTES_PER_DAY = 24 * 60

/**
 * Computes the duration in minutes between two HH:MM or HH:MM:SS times,
 * handling slots that cross midnight.
 *
 * - Same-day slot: `end - start` (e.g. 08:00 → 17:00 = 540 min).
 * - Slot crossing midnight (`end < start`): `(1440 - start) + end`
 *   (e.g. 22:00 → 03:00 = 300 min).
 * - Start equal to end returns 0 (empty slot, never 1440).
 * - Seconds are parsed for validation but truncated from the duration.
 *
 * Any invalid, empty or out-of-range input returns 0. Unlike the PHP V1
 * which silently accepted values such as "25:00" or "10:99", V2 rejects
 * them explicitly — we reproduce the intent of the rule, not its bugs.
 *
 * Why: quarter-hour billing needs a pure, deterministic duration that
 * never relies on `Date`, the system clock, or time zones.
 */
export function durationAcrossMidnight(start: string, end: string): number {
  const startMinutes = parseHhmmToMinutes(start)
  const endMinutes = parseHhmmToMinutes(end)

  if (startMinutes === null || endMinutes === null) {
    return 0
  }

  const diff = endMinutes - startMinutes
  return diff >= 0 ? diff : diff + MINUTES_PER_DAY
}
